import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormActions, Modal, Row, Title } from 'ui';
import { timezones } from 'common/utils';

import type { DeliveryFormData, PersonalFormData } from '../';
import { genders, imperialUnits, languages } from '../../utils';
import { SummaryLine } from '../summary-line';

type P = {
  data: {
    delivery?: DeliveryFormData;
    personal?: PersonalFormData;
  };
  onBack: () => void;
  onSubmit: () => void;
};

const PersonalInfo = ({ data, onBack, onSubmit }: P) => {
  const { t } = useTranslation();

  const generateAddress = () => {
    const address = [data.delivery?.city, data.delivery?.zip, data.delivery?.state].filter(Boolean).join(', ');

    return (
      <>
        <Row justify="end">
          <Col>{data.delivery?.address1}</Col>
        </Row>
        {data.delivery?.address2 ? (
          <Row justify="end">
            <Col>{data.delivery.address2}</Col>
          </Row>
        ) : null}
        <Row justify="end">
          <Col>{address}</Col>
        </Row>
        <Row justify="end">
          <Col>{data.delivery?.country}</Col>
        </Row>
      </>
    );
  };

  const gender = useMemo(
    () => genders.find((gen) => gen.value === data.personal?.gender)?.label,
    [data.personal?.gender],
  );

  const language = useMemo(
    () => languages.find((lang) => lang.value === data.personal?.prefferedLanguage)?.label,
    [data.personal?.prefferedLanguage],
  );

  const timezone = useMemo(
    () => timezones.find((timezoneOption) => timezoneOption.value === data.personal?.prefferedTimezone)?.label,
    [data.personal?.prefferedTimezone],
  );

  const { heightInImperial, weightInImperial } = useMemo(() => {
    if (data.personal) {
      return {
        heightInImperial: imperialUnits.units.height.format(
          data.personal.heightInImperial.feet,
          data.personal.heightInImperial.inches,
        ),
        weightInImperial: imperialUnits.units.weight.format(data.personal.weightInImperial),
      };
    }

    return {};
  }, [data.personal]);

  return (
    <>
      <Title level={4} marginBottom>
        Summary
      </Title>

      <Title level={5} marginBottom>
        Personal information
      </Title>
      <SummaryLine label="Name" value={`${data.personal?.firstName} ${data.personal?.lastName}`} />
      <SummaryLine
        label="Date of birth"
        value={data.personal?.birthdate.date ? format(data.personal.birthdate.date, 'MM/dd/yyyy') : 'Unknown'}
      />
      <SummaryLine label="My timezone" value={timezone} />
      <SummaryLine label="Gender" value={gender ? t(gender) : gender} />
      <SummaryLine label="Weight" value={weightInImperial} />
      <SummaryLine label="Height" value={heightInImperial} />
      <SummaryLine label="Language" underline={false} value={language} />

      <Title level={5} marginBottom>
        Delivery information
      </Title>
      <SummaryLine label="Name" value={`${data.delivery?.firstName} ${data.delivery?.lastName}`} />
      <SummaryLine label="E-mail" value={data.delivery?.email} />
      <SummaryLine label="Phone number" value={data.delivery?.phone} />
      <SummaryLine label="Address" underline={false} value={generateAddress()} />

      <Modal.HugContent.Footer>
        <FormActions right={{ onClick: onBack }} submit={{ children: 'Submit', onClick: onSubmit }} />
      </Modal.HugContent.Footer>
    </>
  );
};

export default PersonalInfo;
